import React, { useRef, useState, useEffect, useMemo } from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import SimpleAlert from 'components/SimpleAlert';
import TextArea from 'components/TextArea';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { BreakRulesReason, getBreakRulesReasonForTravel } from 'services/travelBreakRulesReasonService';
import { getReasonsForTravel, Reason } from 'services/travelReasonService';
import { getFormattedPrice } from 'utils/currency';

import ReasonSelect from './components/ReasonSelect';
import { mapCostCenterToReason } from './mapper';
import {
  Container,
  Title,
  ItineraryCard,
  AirplaneIcon,
  ItineraryText,
  Value,
  ButtonContainer,
  StyledField,
  StyledButton,
  CarIcon,
  HotelIcon,
  NoShowText,
  NoShowTextImportant,
} from './styles';

interface Props {
  show: boolean;
  itineraryValue?: number;
  currencyCode?: string;
  loading: boolean;
  isValid: boolean;
  onClose: () => void;
  onSubmit: () => void;
  productType: ProductType;
  costCenters?: CostCenter[];
  breakRulesMessage?: string | string[];
}

const TravelJustify = ({
  show,
  onClose,
  isValid,
  loading,
  currencyCode,
  itineraryValue,
  onSubmit,
  productType,
  costCenters,
  breakRulesMessage,
}: Props) => {
  const { t } = useStoreTranslation();
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [breakRulesReasons, setBreakRulesReasons] = useState<BreakRulesReason[]>([]);
  const reasonSelectRef = useRef(null);
  const costCenterSelectRef = useRef(null);
  const breakRulesReasonSelectRef = useRef(null);

  useEffect(() => {
    getReasonsForTravel().then((res) => {
      setReasons(res.data);
    });

    if (breakRulesMessage) {
      getBreakRulesReasonForTravel().then((res) => {
        setBreakRulesReasons(res.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedValue = useMemo(() => {
    const formattedPrice = (value: number | undefined) => getFormattedPrice({ value, currency: currencyCode, t });
    if (itineraryValue) {
      return formattedPrice(itineraryValue);
    }
    return undefined;
  }, [t, currencyCode, itineraryValue]);

  const handleSelectReason = (reason: Reason) => (reason ? reason.name : '');
  const handleSelectCostCenter = (costCenter: Reason) => (costCenter ? costCenter.name : '');
  const handleSelectBreakRulesReason = (reason: BreakRulesReason) => (reason ? reason.name : '');

  const isBreakRulesMessageValid = () => {
    if (breakRulesMessage && breakRulesMessage.length > 0 && breakRulesReasons && breakRulesReasons.length > 0) {
      if ((document.getElementById('custom_select_breakRulesReason') as HTMLInputElement)?.value === '') {
        return false;
      } else {
        return true;
      }
    }

    return true;
  };

  return (
    <ApplicationOverlayContent show={show} onClose={onClose} sidebar>
      <Desktop>
        <ModalHeader title={t('travelJustify.header')} />
      </Desktop>
      <Mobile>
        <AppBar goBack={onClose}>
          <h5>{t('travelJustify.header')}</h5>
        </AppBar>
      </Mobile>
      <Container>
        <Title>{t('travelJustify.title')}</Title>

        <StyledField name="reason">
          <CustomSelect
            name="reason"
            label={t('travelJustify.reasonSelectLabel')}
            placeHolder={t('travelJustify.reasonSelectLabel')}
            selectRef={reasonSelectRef}
            onChange={handleSelectReason}
            testId="travel-justify-reason"
          >
            {({ ...selectProps }: CustomSelectRenderProps) => (
              <ReasonSelect {...selectProps} selectRef={reasonSelectRef} reasons={reasons} />
            )}
          </CustomSelect>
        </StyledField>

        <StyledField name="justifyForBreakingRules" label={t('travelJustify.justifyLabel')}>
          <TextArea data-testid="travel-justify-textarea" />
        </StyledField>

        {costCenters && costCenters.length > 0 && (
          <StyledField name="costCenter">
            <CustomSelect
              name="costCenter"
              label={t('travelJustify.costCenterLabel')}
              placeHolder={t('travelJustify.costCenterLabel')}
              selectRef={costCenterSelectRef}
              onChange={handleSelectCostCenter}
              testId="travel-justify-cost-center"
            >
              {({ ...selectProps }: CustomSelectRenderProps) => (
                <ReasonSelect
                  {...selectProps}
                  selectRef={costCenterSelectRef}
                  reasons={mapCostCenterToReason(costCenters)}
                />
              )}
            </CustomSelect>
          </StyledField>
        )}

        {breakRulesMessage && breakRulesMessage.length > 0 && breakRulesReasons && breakRulesReasons.length > 0 && (
          <>
            <br />
            {breakRulesMessage.length > 0 && typeof breakRulesMessage !== 'string' && (
              <SimpleAlert
                title={t('travelJustify.breakRulesReason.outOfPolicies')}
                content={
                  <ul>
                    {(breakRulesMessage as string[])?.map((msg) => (
                      <li key={msg}>&bull; {msg}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {typeof breakRulesMessage === 'string' && (
              <SimpleAlert
                title={t('travelJustify.breakRulesReason.outOfPolicies')}
                content={
                  <ul>
                    <li key="breakRulesMessage1">&bull; {breakRulesMessage}</li>
                  </ul>
                }
              />
            )}
            <StyledField name="breakRulesReason">
              <CustomSelect
                name="breakRulesReason"
                placeHolder={t('travelJustify.breakRulesReason.selectLabel')}
                selectRef={breakRulesReasonSelectRef}
                onChange={handleSelectBreakRulesReason}
                testId="travel-justify-breakreason"
              >
                {({ ...selectProps }: CustomSelectRenderProps) => (
                  <ReasonSelect {...selectProps} selectRef={breakRulesReasonSelectRef} reasons={breakRulesReasons} />
                )}
              </CustomSelect>
            </StyledField>
          </>
        )}

        <ItineraryCard>
          {productType === ProductType.Vehicle && <CarIcon />}
          {productType === ProductType.Flight && <AirplaneIcon src="/images/booking-checkout-airplane.svg" alt="" />}
          {productType === ProductType.Hotel && <HotelIcon />}

          <ItineraryText>{t('travelJustify.itinerary')}</ItineraryText>
          <Value>
            <b>{formattedValue?.formattedWithoutCode}</b>
            {formattedValue?.currency}
          </Value>
        </ItineraryCard>
        {productType === ProductType.Hotel && (
          <>
            <NoShowTextImportant>{t('travelJustify.noShowTextImportant')}</NoShowTextImportant>
            <NoShowText>{t('travelJustify.noShowText')}</NoShowText>
          </>
        )}
        <ButtonContainer>
          <StyledButton
            type="submit"
            onClick={onSubmit}
            value={t('travelJustify.submitText')}
            disabled={!isValid || loading || !isBreakRulesMessageValid()}
            loading={loading}
            testId="travel-justify-submit-button"
          />
        </ButtonContainer>
      </Container>
    </ApplicationOverlayContent>
  );
};

export default TravelJustify;
